// src/app/shared-grid/renderers/cell-renderer-text-undo.ts
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {ICellRendererParams} from 'ag-grid-community';
import {Store} from '@ngrx/store';
import {
  setEditedItem,
  setEditedItemsToStockPage,
  setPriceGpForStockManager,
} from 'src/app/features/stock-manager/store/stock-manager.actions';
import {GridUtils} from 'src/app/shared-utilities/utils-old/grid-utils-old/grid-utils';
import {isEdited} from '../../../features/stock-manager/models/stock-manager-grid-col-def';
import {formatStockManagerEditedCellDisplayValue} from '../../../features/stock-manager/utils/stock-manager-update.utils';
import {formatThousands} from '../../../shared-utilities/utils-old/formatting';
import {NGPReport} from '../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';

/**
 * Reusable text renderer:
 *  • Shows plain value when unedited
 *  • Adds backspace-icon when edited
 *  • On click → reverts the field, updates grid + NgRx, clears highlight
 *
 * Requires:
 *  • gridOptions.context = { store }
 *  • getRowId configured (e.g. row.code) so applyTransaction can find the row
 */
export function CELL_RENDERER_TEXT_STOCK_MANAGER(p: ICellRendererParams): HTMLElement {
  // ---------------------------------------------------------------------
  // 0️⃣ helpers & early exit
  // ---------------------------------------------------------------------
  const store = (p.context as { store: Store }).store;
  const field = p.colDef.field;
  const value = p.value;

  if (!isEdited(p)) {
    const s = document.createElement('span');
    s.textContent = Array.isArray(value) ? value.join(', ') : `${value ?? ''}`;
    return s;
  }

  // ---------------------------------------------------------------------
  // 1️⃣ Build the DOM for edited cells
  // ---------------------------------------------------------------------
  const span = document.createElement('span');
  span.style.cssText =
    'display:flex;align-items:center;gap:4px;width:100%;height:35px;min-width:0;';
  const textSpan = document.createElement('span');
  textSpan.style.cssText =
    'flex:1 1 auto;min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;';

  // Always get the most current value from data
  const currentValue = p.data[field];
  textSpan.textContent = formatStockManagerEditedCellDisplayValue(field, currentValue);

  const iconsContainer = document.createElement('span');
  iconsContainer.style.cssText = 'display:flex;flex:0 0 auto;align-items:center;gap:4px;';

  const icon = document.createElement('app-icon-backspace-outline');
  icon.className = 'icon-backspace-outline';
  icon.title = 'Undo change';
  span.appendChild(textSpan);
  iconsContainer.appendChild(icon);

  if (field === 'regularSuppCode' && String(currentValue ?? '').trim().length > 0) {
    const deleteIcon = document.createElement('app-icon-delete-outline');
    deleteIcon.className = 'icon-delete-outline';
    deleteIcon.title = 'Clear supplier';
    iconsContainer.appendChild(deleteIcon);

    deleteIcon.addEventListener('click', (event: Event) => {
      event.stopPropagation();
      const newData = {
        ...p.node.data,
        [field]: '',
        originalValue: {
          ...p.node.data.originalValue,
          [field]: {value: p.node.data.originalValue[field].value},
        },
      };
      GridUtils.updateSingleItem(newData as StockItem, field, '', store, 'stock-manager');
      GridUtils.updateIsEditedAndForceRefresh({...p, data: newData});
    });
  }

  span.appendChild(iconsContainer);

  // ---------------------------------------------------------------------
  // 2️⃣ Click-handler → revert & sync state
  // ---------------------------------------------------------------------
  icon.addEventListener('click', () => {
    const orig = (p.api.getRowNode(p.node.id).data).originalValue?.[field]?.value ?? '';
    // 2-A Build NEW unfrozen row object
    const newData = {
      ...p.node.data,            // shallow clone
      [field]: orig,
      value: orig,
      originalValue: {
        ...p.node.data.originalValue,
        [field]: {value: orig},
      },
    };

    GridUtils.updateIsEditedAndForceRefresh({...p, data: newData});
    GridUtils.removeIsEditingFlagAndDispatch(newData as StockItem, p.colDef.field, store, 'stock-manager');

  });

  return span;
}

export function CELL_RENDERER_CURRENCY_STOCK_MANAGER(
  p: ICellRendererParams,
): HTMLElement {
  const store = (p.context as { store: Store }).store;
  const field = p.colDef.field;

  /* ──────────────────────────────── 0. Format current value */
  const decimalPlaces: number =
    p.colDef.cellRendererParams?.dataAdditional?.decimalPlaces ?? 2;

  const numValue = +p.value;
  const text = isNaN(numValue) ? '' : formatThousands(numValue, decimalPlaces);

  const span = document.createElement('span');
  span.style.cssText =
    'display:flex;align-items:center;width:100%;height:35px;justify-content:flex-end';
  span.textContent = text;

  if (!isEditedCurrency(p)) {
    return span;
  }

  if (field === 'diffGP' || field === 'nominalGP') {
    span.classList.add('edited');
    return span;
  }

  const icon = document.createElement('app-icon-backspace-outline');
  icon.className = 'icon-backspace-outline';
  span.appendChild(icon);
  span.classList.add('edited');

  icon.addEventListener('click', () => {
    const orig = (p.node.data).originalValue[field].value;

    const newData = {
      ...p.node.data,
      [field]: orig,
      originalValue: {
        ...p.node.data.originalValue,
        [field]: {value: orig},
      },
    };

    if (field === 'sellPriIncl1' || field === 'nominalGP') {
      store.dispatch(setPriceGpForStockManager({stockItem: newData, field}));
    } else {
      store.dispatch(setEditedItem({stockItem: newData}));
      store.dispatch(setEditedItemsToStockPage());
    }

    GridUtils.updateIsEditedAndForceRefresh({...p, data: newData});
  });

  return span;
}

export function CELL_RENDERER_CURRENCY_NGP_REPORT(
  p: ICellRendererParams,
): HTMLElement {
  const store = (p.context as { store: Store }).store;
  const field = p.colDef.field;

  /* ──────────────────────────────── 0. Format current value */
  const decimalPlaces: number =
    p.colDef.cellRendererParams?.dataAdditional?.decimalPlaces ?? 2;

  const numValue = +p.value;
  const text = isNaN(numValue) ? '' : formatThousands(numValue, decimalPlaces);

  const span = document.createElement('span');
  span.style.cssText =
    'display:flex;align-items:flex-end;width:100%;height:35px;justify-content:flex-end;';
  span.textContent = text;

  if (!isEditedCurrency(p)) {
    return span;
  }

  /* diffGP & nominalGP are calculated/read-only on NGP → no undo icon */
  if (field === 'diffGP' || field === 'nominalGP') {
    span.classList.add('edited');
    return span;
  }

  /* ───── 2. Add undo icon */
  const icon = document.createElement('app-icon-backspace-outline');
  icon.className = 'icon-backspace-outline-ngp';
  span.appendChild(icon);
  span.classList.add('edited');

  /* ───── 3. Click handler – revert & clear edited flag */
  icon.addEventListener('click', () => {
    const orig = (p.node.data).originalValue[field].value;

    /* 3A  Build NEW unfrozen row object */
    const newData = {
      ...p.node.data,
      [field]: orig,
      originalValue: {
        ...p.node.data.originalValue,
        [field]: {value: orig},
      },
    };

    /* If the field is price or GP, also rewind dependent values */
    if (field === 'sellPriIncl1' || field === 'nominalGP') {
      ['diffGP', 'sellPriIncl1', 'nominalGP'].forEach(k => {
        if (newData.originalValue[k]) {
          newData[k] = newData.originalValue[k].value;
        }
      });
    }
    /* 3C  Clear global “isEditing” on the NGP item */
    void GridUtils.removeIsEditingFlagFromNgpItem(
      newData as NGPReport,
      field,
      store,
    );

    /* 3D  Refresh highlight */
    GridUtils.updateIsEditedAndForceRefresh({...p, data: newData});
  });

  return span;
}

/* ──────────────────────────────────────────────────────────────────────
 * Helper – is the currency cell edited vs. its original value?
 * ──────────────────────────────────────────────────────────────────── */
function isEditedCurrency(p: ICellRendererParams): boolean {
  const orig = p.data?.originalValue?.[p.colDef.field]?.value;
  const cur = +p.value;
  return +orig !== cur;
}

